/**
 * /partner/kunden – Liste aller über den Partner-Link gewonnenen Kunden.
 * E-Mail wird datenschutzkonform maskiert (z.B. "ab***@example.de").
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PartnerLayout from "@/components/partner/PartnerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users } from "lucide-react";

interface ReferralRow {
  referral_id: string;
  created_at: string;
  attribution_source: string | null;
  masked_email: string | null;
  total_commission_cents: number | null;
  last_status: string | null;
  product_type: string | null;
}

const formatEur = (cents: number | null) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(((cents ?? 0) as number) / 100);

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));

const statusLabel = (s: string | null): { label: string; variant: "secondary" | "default" | "outline" | "destructive" } => {
  switch (s) {
    case "awaiting_payment":
      return { label: "Wartet auf erste Zahlung", variant: "secondary" };
    case "pending":
      return { label: "Offen (Widerruf)", variant: "secondary" };
    case "approved":
      return { label: "Auszahlbar", variant: "default" };
    case "paid":
      return { label: "Ausgezahlt", variant: "outline" };
    case "refunded":
      return { label: "Erstattet", variant: "destructive" };
    case "canceled":
      return { label: "Storniert", variant: "destructive" };
    default:
      return { label: "Noch keine Zahlung", variant: "secondary" };
  }
};

const productLabel = (p: string | null): string => {
  switch (p) {
    case "subscription_monthly":
      return "Mitgliedschaft (monatlich)";
    case "subscription_yearly":
      return "Mitgliedschaft (jährlich)";
    case "subscription_other":
      return "Mitgliedschaft";
    case "one_time_purchase":
      return "Einmalkauf";
    default:
      return "—";
  }
};

const PartnerKunden = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["partner-referrals"],
    queryFn: async (): Promise<ReferralRow[]> => {
      const { data, error } = await supabase.rpc("get_my_affiliate_referrals");
      if (error) throw error;
      return (data ?? []) as ReferralRow[];
    },
    staleTime: 30_000,
  });

  return (
    <PartnerLayout title="Kunden">
      <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
        Hier siehst du alle Personen, die über deinen Partner-Link gekauft haben. Aus Datenschutzgründen
        zeigen wir die E-Mail-Adressen nur teilweise an. Abgebrochene Anfragen verschwinden nach 14&nbsp;Tagen automatisch.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Kunden konnten nicht geladen werden.
          </CardContent>
        </Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Users className="h-10 w-10 text-muted-foreground" />
            <div className="text-base font-medium">Noch keine Kunden</div>
            <p className="max-w-md text-sm text-muted-foreground">
              Sobald jemand über deinen Partner-Link kauft und die erste Rechnung bezahlt ist,
              erscheint die Person hier inklusive Provision.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Datum</th>
                    <th className="px-4 py-3">Kunde</th>
                    <th className="px-4 py-3">Produkt</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Provision</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r) => {
                    const st = statusLabel(r.last_status);
                    return (
                      <tr key={r.referral_id} className="border-b last:border-0">
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(r.created_at)}</td>
                        <td className="px-4 py-3 font-mono text-xs">{r.masked_email ?? "—"}</td>
                        <td className="px-4 py-3">{productLabel(r.product_type)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={st.variant}>{st.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatEur(r.total_commission_cents)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </PartnerLayout>
  );
};

export default PartnerKunden;
